import React, { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { fetchWithAuth } from "../config/api";

export default function BulkMessenger() {
  const { access, refreshAccessToken } = useAuth();

  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [msgEN, setMsgEN] = useState("");
  const [msgAR, setMsgAR] = useState("");
  const [status, setStatus] = useState("");

  // Range selection
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");

  // File attachment (photo or other)
  const [file, setFile] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        // check permission
        const permRes = await fetchWithAuth("/api/user/permissions/");
        if (permRes.status === 401 || permRes.status === 403) {
          setAllowed(false);
          setLoading(false);
          return;
        }
        if (permRes.ok) {
          const p = await permRes.json();
          setAllowed(Boolean(p?.can_send_bulk));
        }

        // load customers (simple list)
        const cRes = await fetchWithAuth("/api/customers/");
        if (cRes.ok) {
          const arr = await cRes.json();
          setCustomers(Array.isArray(arr) ? arr : []);
        }
      } catch (e) {
        console.error("bulk messenger init error", e);
      } finally {
        setLoading(false);
      }
    };
    init();
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectRange = () => {
    const start = parseInt(fromId, 10);
    const end = parseInt(toId, 10);
    if (Number.isNaN(start) || Number.isNaN(end)) {
      alert("Please enter valid numeric IDs.");
      return;
    }
    if (end < start) {
      alert("To ID must be greater than or equal to From ID.");
      return;
    }

    const idsInRange = customers
      .filter((c) => c.id >= start && c.id <= end)
      .map((c) => c.id);

    if (idsInRange.length === 0) {
      alert("No customers found in that ID range.");
      return;
    }

    setSelected((prev) => {
      const set = new Set(prev);
      idsInRange.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  const clearSelection = () => {
    setSelected([]);
  };

  const send = async (e) => {
    e.preventDefault();
    setStatus("");

    const token = access || (await refreshAccessToken());
    if (!token) return;

    if (selected.length === 0) {
      setStatus("Please select at least one customer.");
      return;
    }

    let mediaUrl = null;

    // 1) If a file is selected, upload it first
    if (file) {
      try {
        const formData = new FormData();
        formData.append("file", file);

        const uploadRes = await fetchWithAuth("/api/upload-media/", {
          method: "POST",
          body: formData,
        });

        if (uploadRes.status === 401 || uploadRes.status === 403) {
          setStatus("Your session has expired. Please log in again.");
          return;
        }

        if (!uploadRes.ok) {
          setStatus("Failed to upload attachment.");
          return;
        }

        const uploadData = await uploadRes.json();
        mediaUrl = uploadData.url;
      } catch (err) {
        console.error("Upload error", err);
        setStatus("Error uploading attachment.");
        return;
      }
    }

    // 2) Build payload for bulk-send
    const payload = {
      customer_ids: selected,
      message: {
        EN: msgEN || undefined,
        AR: msgAR || undefined,
        media_url: mediaUrl || undefined,
      },
    };

    // 3) Call bulk-send
    const res = await fetchWithAuth("/api/bulk-send/", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (res.status === 403) {
      setStatus("You are not authorized to send bulk messages.");
      setAllowed(false);
      return;
    }

    const ok = res.ok;
    let msg = ok ? "Queued ✅" : "Failed to queue";
    try {
      const j = await res.json();
      if (!ok && j?.error) msg = j.error;
      if (ok && typeof j?.recipients === "number") {
        msg = `Queued ✅ (${j.recipients} recipients)`;
      }
    } catch {
      // ignore JSON parse errors
    }
    setStatus(msg);
  };

  if (loading) return <div className="card">Loading…</div>;
  if (!allowed) {
    return (
      <div className="card">
        <h3>Bulk Messaging</h3>
        <div>You don’t have permission to use this feature.</div>
      </div>
    );
  }

  return (
    <div className="card">
      <h3>Bulk Messaging</h3>

      <form onSubmit={send} style={{ display: "grid", gap: 12 }}>
        {/* Range selection + totals */}
        <div>
          <label className="label">Select by Customer ID Range</label>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <input
              type="number"
              className="input"
              placeholder="From ID"
              value={fromId}
              onChange={(e) => setFromId(e.target.value)}
              style={{ maxWidth: 120 }}
            />
            <span>to</span>
            <input
              type="number"
              className="input"
              placeholder="To ID"
              value={toId}
              onChange={(e) => setToId(e.target.value)}
              style={{ maxWidth: 120 }}
            />
            <button
              type="button"
              className="btn"
              onClick={selectRange}
            >
              Select Range
            </button>
            <button
              type="button"
              className="btn"
              onClick={clearSelection}
            >
              Clear Selection
            </button>
          </div>
          <div>
            <b>Total selected:</b> {selected.length}
          </div>
        </div>

        {/* Recipients list */}
        <div>
          <label className="label">Recipients</label>
          <div
            style={{
              maxHeight: 200,
              overflow: "auto",
              border: "1px solid #eee",
              padding: 8,
            }}
          >
            {customers.map((c) => (
              <label key={c.id} style={{ display: "block", marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={selected.includes(c.id)}
                  onChange={() => toggle(c.id)}
                />
                <span style={{ marginLeft: 8 }}>
                  #{c.id} — {c.name} — {c.phone}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Message fields */}
        <div>
          <label className="label">Message (EN)</label>
          <textarea
            className="input"
            rows={3}
            value={msgEN}
            onChange={(e) => setMsgEN(e.target.value)}
          />
        </div>

        <div>
          <label className="label">Message (AR)</label>
          <textarea
            className="input"
            rows={3}
            value={msgAR}
            onChange={(e) => setMsgAR(e.target.value)}
          />
        </div>

        {/* File attachment */}
        <div>
          <label className="label">Attachment (photo / file)</label>
          <input
            type="file"
            accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setFile(f);
            }}
          />
          {file && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              Selected file: <b>{file.name}</b>
            </div>
          )}
        </div>

        <button className="btn" type="submit">
          Send
        </button>
      </form>

      {status && <div style={{ marginTop: 10 }}>{status}</div>}
    </div>
  );
}
